import { useQuery } from "@tanstack/react-query";
import { fetchReportStateHistory } from "../../api/reportsApi";
import { ReportState } from "../../types/Rows";
import { MdExpandMore } from "react-icons/md";
import { useState } from "react";

const STATUS_META: Record<number, { label: string; pill: string; dot: string }> = {
  [ReportState.INPROGRESS]: { label: "W trakcie",       pill: "bg-[#FEF3E2] text-[#C07810]", dot: "#F4A832" },
  [ReportState.TO_VERIFY]:  { label: "Do weryfikacji",  pill: "bg-[#EDF3FC] text-[#3570C4]", dot: "#5B8FD8" },
  [ReportState.TO_CORRECT]: { label: "Do poprawy",      pill: "bg-[#FEE8E8] text-[#C03030]", dot: "#E05050" },
  [ReportState.ACCEPTED]:   { label: "Zaakceptowano",   pill: "bg-[#E6F7F1] text-[#1A8055]", dot: "#2BAE72" },
};

const STATE_UNKNOWN = { label: "Nieznany", pill: "bg-[#F3F3F2] text-[#6B6B69]", dot: "#ADADAA" };

function StatePill({ state }: { state: number | null }) {
  const meta = state != null ? (STATUS_META[state] ?? STATE_UNKNOWN) : STATE_UNKNOWN;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.pill}`}>
      {meta.label}
    </span>
  );
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString("pl-PL", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function shortName(email: string) {
  if (!email) return "—";
  const local = email.split("@")[0];
  const parts = local.split(/[._-]/);
  if (parts.length >= 2) {
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
  }
  return local;
}

export function ReportStateTimeline({ reportId }: { reportId: string }) {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery(
    [reportId, "StateHistory"],
    () => fetchReportStateHistory(reportId),
    { enabled: !!reportId }
  );

  const isEmpty = !data || data.length === 0;

  return (
    <div className="rounded-[10px] border border-[var(--sc-border)] bg-[var(--sc-surface)] overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3 border-b border-[var(--sc-border)] text-left"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="flex items-center gap-1.5">
          <MdExpandMore
            size={18}
            className="text-[var(--sc-muted)] transition-transform duration-200"
            style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)" }}
          />
          <span className="text-[15px] font-semibold text-[var(--sc-text)]">Historia statusów</span>
          {!isEmpty && (
            <span className="text-[11px] text-[var(--sc-muted)]">
              {expanded ? "ukryj" : `${data.length} ${data.length === 1 ? "zmiana" : "zmiany"}`}
            </span>
          )}
        </span>
      </button>

      {expanded && (
        <div className="px-5 py-4">
          {isLoading && (
            <p className="text-[13px] text-[var(--sc-muted)]">Ładowanie...</p>
          )}
          {!isLoading && isEmpty && (
            <p className="text-[13px] text-[var(--sc-muted)]">Brak zarejestrowanych zmian statusu.</p>
          )}
          {!isLoading && !isEmpty && (
            <ol className="relative">
              {data.map((t, i) => {
                const dotColor = t.toState != null
                  ? (STATUS_META[t.toState]?.dot ?? STATE_UNKNOWN.dot)
                  : STATE_UNKNOWN.dot;
                const isLast = i === data.length - 1;
                return (
                  <li key={i} className="flex gap-4 pb-5 last:pb-0">
                    {/* Dot + connector */}
                    <div className="flex flex-col items-center flex-shrink-0 w-3">
                      <div
                        className="w-3 h-3 rounded-full ring-2 ring-white flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: dotColor }}
                      />
                      {!isLast && (
                        <div className="w-px flex-1 bg-[var(--sc-border)] mt-1" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex flex-col gap-1 min-w-0 pb-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {t.fromState != null && (
                          <>
                            <StatePill state={t.fromState} />
                            <span className="text-[11px] text-[var(--sc-muted)]">→</span>
                          </>
                        )}
                        <StatePill state={t.toState} />
                      </div>
                      <p className="text-[12px] text-[var(--sc-muted)]">
                        {fmtDate(t.date)}
                        {t.changedBy && (
                          <> &middot; <span title={t.changedBy}>{shortName(t.changedBy)}</span></>
                        )}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
